import type { NavSearch } from '@assessments/shared'

/**
 * Resolve the platform origin a relative search destination is resolved against.
 * Derived from the configured nav-feed URL. Returns undefined when the value is
 * absent or not an absolute URL (e.g. a relative configured value), so callers can
 * decline to render rather than throw.
 */
export function resolvePlatformOrigin(navUrl: string | undefined): string | undefined {
  if (!navUrl) return undefined
  try {
    return new URL(navUrl).origin
  } catch {
    return undefined
  }
}

/**
 * Whether `search.url` resolves to a destination on the platform origin. Requires a
 * `platformOrigin` (an unconfigured base means "don't render", FR-009) and clamps the
 * handoff to it: a relative url is resolved against it, an absolute url is accepted
 * only when it is same-origin. Keeps a typed query from being handed off to a foreign
 * origin even if the nav feed names one.
 */
export function canResolveSearchUrl(url: string, platformOrigin: string | undefined): boolean {
  if (!platformOrigin) return false
  try {
    return new URL(url, platformOrigin).origin === platformOrigin
  } catch {
    return false
  }
}

/**
 * Build the search-handoff URL from the feed's search config and the typed query.
 * Resolves `search.url` against `platformOrigin`, URL-encodes the query, and preserves
 * any existing query string on the destination. Throws on an unresolvable or off-origin
 * destination (callers guard with `canResolveSearchUrl` first); the empty-query case is
 * the caller's to guard.
 */
export function buildSearchUrl(
  platformOrigin: string | undefined,
  search: NavSearch,
  query: string,
): string {
  if (!canResolveSearchUrl(search.url, platformOrigin)) {
    throw new Error('navSearch: refusing to build an off-origin or unresolvable search URL')
  }
  const url = new URL(search.url, platformOrigin)
  url.searchParams.set(search.field, query.trim())
  return url.toString()
}
